import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  getMidpointMediaNotificationSend,
  listMidpointMediaNotificationSends,
  listMidpointMediaNotificationTemplates,
} from "../../api/midpointMediaAdminApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { MIDPOINT_MEDIA_ADMIN_PORTAL_BASE } from "../../constants/clientAdminPortalRoutes";
import { useClientPortalBase, useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { formatClientDateTime } from "../../utils/clientPortalFormat";

function statusLabel(status) {
  if (status === "sent") return "Sent";
  if (status === "console") return "Console";
  if (status === "failed") return "Failed";
  if (status === "disabled") return "Disabled";
  return status || "—";
}

function statusClass(status) {
  if (status === "sent") return "cp-notify-status cp-notify-status--sent";
  if (status === "console") return "cp-notify-status cp-notify-status--console";
  if (status === "failed" || status === "disabled") return "cp-notify-status cp-notify-status--failed";
  return "cp-notify-status";
}

function smtpFrom(send) {
  return send?.metadata?.smtp || {};
}

function explainSendFailure(send) {
  const error = String(send?.error || "");
  const smtp = smtpFrom(send);
  const haystack = [error, smtp.message, smtp.response, smtp.code, send?.toEmail]
    .filter(Boolean)
    .join(" ");
  if (/Invalid `to` field|example\.com/i.test(haystack)) {
    return "The email provider rejected the recipient address. Resend will not deliver to domains like example.com. Use a real inbox.";
  }
  if (error === "template_disabled") {
    return "This notification is turned off, so nothing was sent.";
  }
  if (error === "console_provider") {
    return "EMAIL_PROVIDER is console, so the message was written to server logs instead of an inbox.";
  }
  if (error === "missing_smtp_host") {
    return "SMTP is selected but SMTP_HOST is not configured.";
  }
  if (error === "missing_from_address") {
    return "EMAIL_FROM_ADDRESS is missing or invalid.";
  }
  if (error === "unsupported_provider") {
    return "The configured email provider is not supported.";
  }
  if (smtp.responseCode === 550 || /\b550\b/.test(haystack)) {
    return "The provider refused the message (SMTP 550). Check the recipient address, domain authentication, or provider restrictions.";
  }
  if (smtp.code === "ECONNECTION" || smtp.code === "ETIMEDOUT" || smtp.syscall) {
    return "The app could not connect to the SMTP host. Check SMTP_HOST, port, TLS, and network access.";
  }
  if (error === "provider_error" || smtp.message) {
    return "The SMTP provider rejected the message. See the technical details below.";
  }
  if (!error) {
    return "Delivery failed without a provider error message.";
  }
  return error;
}

function detailRows(send) {
  const smtp = smtpFrom(send);
  const meta = send?.metadata || {};
  const rows = [
    ["Status", statusLabel(send.status)],
    ["Sent at", formatClientDateTime(send.createdAt)],
    ["Template", send.templateName || send.templateKey],
    ["To", send.toEmail],
    ["Subject", send.subject],
    ["Provider", send.provider || "—"],
    ["From", meta.fromAddress ? `${meta.fromName || ""} <${meta.fromAddress}>`.trim() : "—"],
    ["SMTP host", meta.smtpHost ? `${meta.smtpHost}${meta.smtpPort ? `:${meta.smtpPort}` : ""}` : "—"],
    ["Error", send.error || "—"],
    ["Error code", meta.errorCode || "—"],
    ["SMTP name", smtp.name || "—"],
    ["SMTP code", smtp.code || "—"],
    ["SMTP command", smtp.command || "—"],
    ["SMTP response code", smtp.responseCode || "—"],
    ["SMTP response", smtp.response || smtp.message || "—"],
    ["Network", smtp.syscall ? `${smtp.syscall}${smtp.address ? ` ${smtp.address}` : ""}${smtp.port ? `:${smtp.port}` : ""}` : "—"],
    ["Accepted", Array.isArray(meta.accepted) && meta.accepted.length ? meta.accepted.join(", ") : "—"],
    ["Rejected recipients", Array.isArray(smtp.rejected) && smtp.rejected.length
      ? smtp.rejected.join(", ")
      : Array.isArray(meta.rejected) && meta.rejected.length
        ? meta.rejected.join(", ")
        : "—"],
    ["Message ID", meta.messageId || "—"],
    ["Send ID", send.sendId],
  ];
  return rows;
}

function SendLogTable({ rows, onOpen }) {
  return (
    <div className="cp-settings-team-table-wrap">
      <table className="cp-settings-team-table">
        <thead>
          <tr>
            <th>Sent</th>
            <th>Template</th>
            <th>To</th>
            <th>Subject</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const failed = row.status === "failed" || row.status === "disabled";
            return (
              <tr
                key={row.sendId}
                className={`cp-notify-row${failed ? " cp-notify-row--failed" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen(row);
                  }
                }}
              >
                <td>{formatClientDateTime(row.createdAt)}</td>
                <td>{row.templateName}</td>
                <td className="cp-settings-email">{row.toEmail}</td>
                <td>{row.subject}</td>
                <td>
                  <span className={statusClass(row.status)}>{statusLabel(row.status)}</span>
                  {row.error ? <div className="cp-notify-error">{row.error}</div> : null}
                  {failed ? <div className="muted">View details</div> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NotificationSendDetailModal({ clientViewId, send, onClose }) {
  const [detail, setDetail] = useState(send);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const body = await getMidpointMediaNotificationSend(clientViewId, send.sendId);
        if (!cancelled) setDetail(body.send || send);
      } catch {
        if (!cancelled) setDetail(send);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [clientViewId, send]);

  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const failed = detail.status === "failed" || detail.status === "disabled";
  const explanation = failed ? explainSendFailure(detail) : null;
  const smtp = smtpFrom(detail);
  const rejectedErrors = Array.isArray(smtp.rejectedErrors)
    ? smtp.rejectedErrors.map((item) => (typeof item === "string" ? item : JSON.stringify(item, null, 2)))
    : [];
  const providerText = smtp.response || smtp.message || (detail.error && detail.error !== "provider_error" ? detail.error : "");
  const limitedDiagnostics = failed && !smtp.responseCode && !smtp.code && !smtp.response;

  return (
    <div className="client-vendor-modal-backdrop cp-notify-detail-backdrop" role="presentation" onClick={onClose}>
      <div
        className="client-vendor-modal cp-notify-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-notify-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="client-vendor-modal-header">
          <h3 id="cp-notify-detail-title">{failed ? "Delivery failed" : "Notification send"}</h3>
          <button type="button" className="btn-sm" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="cp-notify-detail-body">
          {failed ? <p className="cp-notify-detail-summary">{explanation}</p> : null}
          {limitedDiagnostics ? (
            <p className="muted">
              This send was logged with a short error only. Newer failures include the SMTP
              command, response code, and provider text.
            </p>
          ) : null}
          {loading ? <p className="muted">Loading technical details…</p> : null}
          <dl className="cp-notify-detail-grid">
            {detailRows(detail).map(([label, value]) => (
              <div key={label} className="cp-notify-detail-row">
                <dt>{label}</dt>
                <dd>{String(value ?? "—")}</dd>
              </div>
            ))}
          </dl>
          {rejectedErrors.length ? (
            <div className="cp-notify-detail-block">
              <h4>Rejected recipient errors</h4>
              <pre>{rejectedErrors.join("\n")}</pre>
            </div>
          ) : null}
          {providerText ? (
            <div className="cp-notify-detail-block">
              <h4>Provider response</h4>
              <pre>{providerText}</pre>
            </div>
          ) : null}
          {smtp && Object.keys(smtp).length ? (
            <div className="cp-notify-detail-block">
              <h4>SMTP diagnostic dump</h4>
              <pre>{JSON.stringify(smtp, null, 2)}</pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function NotificationSearchModal({ clientViewId, onClose, onOpenSend }) {
  const [draft, setDraft] = useState({ q: "", templateKey: "", status: "" });
  const [query, setQuery] = useState({ q: "", templateKey: "", status: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [result, setResult] = useState({ rows: [], pagination: { page: 1, total: 0, totalPages: 0 } });

  const load = useCallback(
    async (nextPage = 1) => {
      setLoading(true);
      setErrorMessage("");
      try {
        const body = await listMidpointMediaNotificationSends(clientViewId, {
          q: query.q,
          templateKey: query.templateKey,
          status: query.status,
          page: nextPage,
          limit: 25,
        });
        setResult({
          rows: body.rows || [],
          pagination: body.pagination || { page: nextPage, total: 0, totalPages: 0 },
        });
        setPage(nextPage);
      } catch (error) {
        setErrorMessage(error.message || "Unable to search notifications.");
      } finally {
        setLoading(false);
      }
    },
    [clientViewId, query]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const pagination = result.pagination || {};

  return (
    <div className="client-vendor-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="client-vendor-modal cp-notify-search-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-notify-search-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="client-vendor-modal-header">
          <h3 id="cp-notify-search-title">Search notifications</h3>
          <button type="button" className="btn-sm" onClick={onClose}>
            Close
          </button>
        </header>
        <form
          className="cp-notify-search-form"
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(draft);
          }}
        >
          <label className="admin-users-field">
            <span>Search</span>
            <input
              type="search"
              value={draft.q}
              onChange={(event) => setDraft((prev) => ({ ...prev, q: event.target.value }))}
              placeholder="Email, subject, template, or error"
            />
          </label>
          <label className="admin-users-field">
            <span>Template</span>
            <select
              value={draft.templateKey}
              onChange={(event) => setDraft((prev) => ({ ...prev, templateKey: event.target.value }))}
            >
              <option value="">All templates</option>
              <option value="ops.employee_invite">Employee invitation</option>
              <option value="ops.password_reset">Password reset</option>
              <option value="ops.careers_application">New job application</option>
            </select>
          </label>
          <label className="admin-users-field">
            <span>Status</span>
            <select
              value={draft.status}
              onChange={(event) => setDraft((prev) => ({ ...prev, status: event.target.value }))}
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="console">Console</option>
              <option value="failed">Failed</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
        {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
        {loading ? (
          <p className="muted">Searching…</p>
        ) : result.rows.length ? (
          <SendLogTable rows={result.rows} onOpen={onOpenSend} />
        ) : (
          <p className="muted">No matching notifications.</p>
        )}
        {pagination.totalPages > 1 ? (
          <div className="cp-notify-pager">
            <button
              type="button"
              className="btn-sm"
              disabled={page <= 1}
              onClick={() => load(page - 1)}
            >
              Previous
            </button>
            <span className="muted">
              Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
            </span>
            <button
              type="button"
              className="btn-sm"
              disabled={page >= pagination.totalPages}
              onClick={() => load(page + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function ClientSettingsNotificationsPage() {
  const { clientViewId } = useParams();
  const portalBase = useClientPortalBase();
  const paths = useClientPortalPaths();
  const isMidpointMedia = portalBase === MIDPOINT_MEDIA_ADMIN_PORTAL_BASE;
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [recent, setRecent] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [detailSend, setDetailSend] = useState(null);

  const load = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const [templateBody, sendBody] = await Promise.all([
        listMidpointMediaNotificationTemplates(clientViewId),
        listMidpointMediaNotificationSends(clientViewId, { limit: 10, page: 1 }),
      ]);
      setTemplates(templateBody.templates || []);
      setDelivery(templateBody.delivery || null);
      setRecent(sendBody.rows || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isMidpointMedia) {
    return <Navigate to={paths.viewSection(clientViewId, "settings")} replace />;
  }

  if (loading) {
    return <ClientPortalLoading message="Loading notifications…" />;
  }

  const settingsBase = paths.viewSection(clientViewId, "settings", "notifications");

  return (
    <>
      <section className="panel-card cp-settings-section-header">
        <h3>Notifications</h3>
        <p className="muted">
          Emails Midpoint Operations sends for employee invites, password resets, and new job
          applications. Edit a template to change the copy. Tags like {"{{displayName}}"} are
          replaced when the message is sent.
        </p>
      </section>

      {delivery?.warning ? (
        <section className="panel-card cp-notify-banner">
          <strong>{delivery.live ? "Live delivery" : "Not delivering to inboxes"}</strong>
          <p className="muted">
            {`Provider: ${delivery.provider}${
              delivery.fromAddress ? ` · From ${delivery.fromName || ""} <${delivery.fromAddress}>` : ""
            }${delivery.smtpHost ? ` · SMTP ${delivery.smtpHost}` : ""}. ${delivery.warning}`}
          </p>
        </section>
      ) : delivery ? (
        <p className="muted cp-notify-delivery">
          {`Live delivery via ${delivery.provider}${
            delivery.fromAddress ? ` from ${delivery.fromName || ""} <${delivery.fromAddress}>` : ""
          }${delivery.smtpHost ? ` (${delivery.smtpHost})` : ""}.`}
        </p>
      ) : null}

      <ClientPortalError message={errorMessage} />

      <section className="panel-card">
        <h3>Outbound notifications</h3>
        <div className="cp-notify-list">
          {templates.map((template) => (
            <Link
              key={template.key}
              className="cp-notify-item"
              to={`${settingsBase}/${encodeURIComponent(template.key)}`}
            >
              <div>
                <strong>{template.name}</strong>
                <p>{template.description}</p>
                <span className="muted">
                  {template.category} · {template.audience} · {template.source}
                  {template.customized ? " · Customized" : ""}
                  {template.enabled ? "" : " · Disabled"}
                </span>
              </div>
              <div className="cp-notify-item__meta">
                {template.lastSend ? (
                  <>
                    <span className={statusClass(template.lastSend.status)}>
                      {statusLabel(template.lastSend.status)}
                    </span>
                    <span className="muted">{formatClientDateTime(template.lastSend.createdAt)}</span>
                  </>
                ) : (
                  <span className="muted">No sends yet</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="panel-card">
        <div className="cp-notify-log-header">
          <div>
            <h3>Recent sends</h3>
            <p className="muted">The last 10 notifications dispatched from this admin.</p>
          </div>
          <button type="button" className="btn-primary" onClick={() => setSearchOpen(true)}>
            Search all notifications
          </button>
        </div>
        {recent.length ? (
          <SendLogTable rows={recent} onOpen={setDetailSend} />
        ) : (
          <p className="muted">No notifications have been sent yet.</p>
        )}
      </section>

      {searchOpen ? (
        <NotificationSearchModal
          clientViewId={clientViewId}
          onClose={() => setSearchOpen(false)}
          onOpenSend={setDetailSend}
        />
      ) : null}
      {detailSend ? (
        <NotificationSendDetailModal
          clientViewId={clientViewId}
          send={detailSend}
          onClose={() => setDetailSend(null)}
        />
      ) : null}
    </>
  );
}
